
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Lichte fetch-wrapper met axios-compatibele interface ({ data, status } / err.response.{status,data}).
class ApiError extends Error {
  constructor(message, response) {
    super(message);
    this.response = response;
  }
}

const toQuery = (params) => {
  if (!params) return "";
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) q.append(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
};

async function request(method, url, body, config = {}) {
  const headers = { Accept: "application/json", ...(config.headers || {}) };
  let payload = body;
  if (body instanceof FormData) {
    delete headers["Content-Type"];
  } else if (body !== undefined) {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
    payload = JSON.stringify(body);
  }
  const res = await fetch(`${API}${url}${toQuery(config.params)}`, {
    method,
    headers,
    body: payload,
    credentials: "include",
    signal: config.signal,
  });
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json().catch(() => null) : await res.text();
  if (!res.ok) {
    throw new ApiError(data?.detail || `Request failed with status code ${res.status}`, { status: res.status, data });
  }
  return { data, status: res.status, headers: res.headers };
}

export const api = {
  get: (url, config) => request("GET", url, undefined, config),
  post: (url, body, config) => request("POST", url, body, config),
  put: (url, body, config) => request("PUT", url, body, config),
  delete: (url, config) => request("DELETE", url, undefined, config),
};

// Data die het boot-script in index.html al ophaalde (window.__ecell) hergebruiken voor de eerste render.
const PRELOAD_KEYS = [
  [/^\/content$/, () => ["content"]],
  [/^\/site-settings$/, () => ["site-settings"]],
  [/^\/service-pages$/, () => ["service-pages"]],
  [/^\/cases$/, () => ["cases"]],
  [/^\/blog$/, () => ["blog"]],
  [/^\/service-pages\/(.+)$/, (m) => ["service-page", m[1]]],
  [/^\/blog\/(.+)$/, (m) => ["blog", m[1]]],
  [/^\/cases\/(.+)$/, (m) => ["case", m[1]]],
  [/^\/pages\/(.+)$/, (m) => ["page", m[1]]],
];

export function seedFromPreload(queryClient) {
  const data = window.__ecell?.data || {};
  Object.entries(data).forEach(([path, value]) => {
    const hit = PRELOAD_KEYS.find(([re]) => re.test(path));
    if (hit) queryClient.setQueryData(hit[1](path.match(hit[0])), value);
  });
}

const getJson = (path) => {
  const pending = window.__ecell?.[path];
  const fresh = () => api.get(path).then((r) => r.data);
  if (!pending) return fresh();
  delete window.__ecell[path];
  return pending.catch(fresh);
};

// Public
export const fetchSiteSettings = () => getJson("/site-settings");
export const fetchContent = () => getJson("/content");
export const fetchCases = () => getJson("/cases");
export const fetchCase = (slug) => getJson(`/cases/${slug}`);
export const fetchBlog = () => getJson("/blog");
export const fetchBlogPost = (slug) => getJson(`/blog/${slug}`);
export const submitContact = (payload) => api.post("/contact", payload).then((r) => r.data);
export const fetchPages = () => api.get("/pages").then((r) => r.data);
export const fetchPage = (slug) => getJson(`/pages/${slug}`);
export const subscribeNewsletter = (email, source = "blog", utm = {}) =>
  api.post("/newsletter", { email, source, ...utm }).then((r) => r.data);
export const trackPageview = (payload) => api.post("/track", payload).then((r) => r.data);

// Auth
export const exchangeSession = (session_id) =>
  api.post("/auth/session", { session_id }).then((r) => r.data);
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data);

// Admin
export const updateSettings = (payload) =>
  api.put("/admin/site-settings", payload).then((r) => r.data);
export const createCase = (payload) => api.post("/admin/cases", payload).then((r) => r.data);
export const updateCase = (id, payload) =>
  api.put(`/admin/cases/${id}`, payload).then((r) => r.data);
export const deleteCase = (id) => api.delete(`/admin/cases/${id}`).then((r) => r.data);
export const createBlogPost = (payload) => api.post("/admin/blog", payload).then((r) => r.data);
export const updateBlogPost = (id, payload) =>
  api.put(`/admin/blog/${id}`, payload).then((r) => r.data);
export const deleteBlogPost = (id) => api.delete(`/admin/blog/${id}`).then((r) => r.data);
export const fetchContactMessages = () =>
  api.get("/admin/contact-messages").then((r) => r.data);
export const deleteContactMessage = (id) =>
  api.delete(`/admin/contact-messages/${id}`).then((r) => r.data);
export const fetchAllPages = () =>
  api.get("/pages", { params: { all: true } }).then((r) => r.data);
export const createPage = (payload) => api.post("/admin/pages", payload).then((r) => r.data);
export const updatePage = (id, payload) =>
  api.put(`/admin/pages/${id}`, payload).then((r) => r.data);
export const deletePage = (id) => api.delete(`/admin/pages/${id}`).then((r) => r.data);

// AI
export async function streamAi(path, payload, onChunk) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Er ging iets mis met de AI-verbinding.");
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    onChunk(decoder.decode(value, { stream: true }));
  }
}
export const fetchBlogSummary = (slug) =>
  api.post("/ai/blog-summary", { slug }).then((r) => r.data);
export const adminAiGenerate = (kind, input) =>
  api.post("/admin/ai/generate", { kind, input }).then((r) => r.data);
export const aiContentText = (payload) =>
  api.post("/admin/ai/content-text", payload).then((r) => r.data);
export const aiSeoCheck = (payload) =>
  api.post("/admin/ai/seo-check", payload).then((r) => r.data);
export const createChatLead = (payload) => api.post("/chat-lead", payload).then((r) => r.data);
export const fetchChatLeads = () => api.get("/admin/chat-leads").then((r) => r.data);
export const deleteChatLead = (id) => api.delete(`/admin/chat-leads/${id}`).then((r) => r.data);
export const aiKeywords = (payload) =>
  api.post("/admin/ai/keywords", payload).then((r) => r.data);
export const aiApplyTips = (payload) =>
  api.post("/admin/ai/apply-tips", payload).then((r) => r.data);
export const fetchScanLeads = () => api.get("/admin/scan-leads").then((r) => r.data);
export const deleteScanLead = (id) => api.delete(`/admin/scan-leads/${id}`).then((r) => r.data);
export const fetchAllServices = () => api.get("/services", { params: { all: true } }).then((r) => r.data);
export const fetchAllCases = () => api.get("/cases", { params: { all: true } }).then((r) => r.data);
export const fetchAllBlog = () => api.get("/blog", { params: { all: true } }).then((r) => r.data);
export const uploadImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const fetchAdminStats = () => api.get("/admin/stats").then((r) => r.data);
export const fetchNewsletterSubs = () => api.get("/admin/newsletter").then((r) => r.data);
export const deleteNewsletterSub = (id) => api.delete(`/admin/newsletter/${id}`).then((r) => r.data);
export const fetchAdminContent = () => api.get("/admin/content").then((r) => r.data);
export const updateContentBlocks = (values) =>
  api.put("/admin/content", { values }).then((r) => r.data);
export const fetchAnalytics = (days = 30) =>
  api.get("/admin/analytics", { params: { days } }).then((r) => r.data);
export const fetchRoadmap = () => api.get("/admin/roadmap").then((r) => r.data);
export const generateRoadmap = () => api.post("/admin/roadmap/generate").then((r) => r.data);
export const createRoadmapItem = (payload) => api.post("/admin/roadmap", payload).then((r) => r.data);
export const updateRoadmapItem = ({ id, payload }) =>
  api.put(`/admin/roadmap/${id}`, payload).then((r) => r.data);
export const deleteRoadmapItem = (id) => api.delete(`/admin/roadmap/${id}`).then((r) => r.data);
export const fetchServicePages = () => getJson("/service-pages");
export const fetchServicePage = (slug) => getJson(`/service-pages/${slug}`);
export const fetchAllServicePages = () =>
  api.get("/service-pages", { params: { all: true } }).then((r) => r.data);
export const createServicePage = (payload) =>
  api.post("/admin/service-pages", payload).then((r) => r.data);
export const updateServicePage = (id, payload) =>
  api.put(`/admin/service-pages/${id}`, payload).then((r) => r.data);
export const deleteServicePage = (id) =>
  api.delete(`/admin/service-pages/${id}`).then((r) => r.data);
export const fetchBlogIdeas = () => api.get("/admin/blog-ideas").then((r) => r.data);
export const fetchSeoReport = (refresh = false) =>
  api.get("/admin/seo-report", { params: refresh ? { refresh: true } : {} }).then((r) => r.data);
export const fetchGscSitemaps = () => api.get("/admin/gsc/sitemaps").then((r) => r.data);
export const submitGscSitemap = () => api.post("/admin/gsc/submit-sitemap").then((r) => r.data);
export const fetchGscStatus = () => api.get("/admin/gsc/status").then((r) => r.data);
export const fetchGscOverview = (days = 28) =>
  api.get("/admin/gsc/overview", { params: { days } }).then((r) => r.data);
// Performance & SEO monitoring
export const fetchPsiResults = () => api.get("/admin/psi/results").then((r) => r.data);
export const fetchPsiPages = () => api.get("/admin/psi/pages").then((r) => r.data);
export const addPsiPage = (payload) => api.post("/admin/psi/pages", payload).then((r) => r.data);
export const deletePsiPage = (id) => api.delete(`/admin/psi/pages/${id}`).then((r) => r.data);
export const fetchPsiHistory = (path, days = 90) =>
  api.get("/admin/psi/history", { params: { path, days } }).then((r) => r.data);
export const measurePsiPage = (payload) => api.post("/admin/psi/measure", payload).then((r) => r.data);
export const measureAllPsi = () => api.post("/admin/psi/measure-all").then((r) => r.data);
export const fetchPsiStatus = () => api.get("/admin/psi/status").then((r) => r.data);
export const fetchSeoChecks = () => api.get("/admin/seo-checks").then((r) => r.data);

export const fetchChatSessions = () => api.get("/admin/chat-sessions").then((r) => r.data);
export const fetchChatSession = (sid) => api.get(`/admin/chat-sessions/${sid}`).then((r) => r.data);
export const deleteChatSession = (sid) => api.delete(`/admin/chat-sessions/${sid}`).then((r) => r.data);
